/** @format */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import BlindRender from "../../components/BlindRender";
import { useDispatch, useSelector } from "react-redux";
import { calculateTotalPrice } from "../../actions/blindActions";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Stepper from "../../components/Stepper";
import { addEmailToCartMailChimp } from "../../actions/userActions";
import { addToCart, removeFromCart } from "../../actions/cartActions";
import { motion, AnimatePresence } from "framer-motion";
import {
	ArrowLeft,
	ArrowRight,
	HelpCircle,
	Eye,
	X as XIcon,
} from "lucide-react"; // Added Eye, XIcon
import { Toast } from "./Toast";

// --- Helper: getSimplyCutSize (Keep as is) ---
export function getSimplyCutSize(width, measure) {
	/* ... function code ... */
	let x;
	let y;
	width = parseFloat(width.toString());
	function roundDownToFraction(input) {
		const fractions = [
			"0",
			"1/16",
			"1/8",
			"3/16",
			"1/4",
			"5/16",
			"3/8",
			"7/16",
			"1/2",
			"9/16",
			"5/8",
			"11/16",
			"3/4",
			"13/16",
			"7/8",
			"15/16",
		];
		const fractionValues = [
			0,
			1 / 16,
			1 / 8,
			3 / 16,
			1 / 4,
			5 / 16,
			3 / 8,
			7 / 16,
			1 / 2,
			9 / 16,
			5 / 8,
			11 / 16,
			3 / 4,
			13 / 16,
			7 / 8,
			15 / 16,
		];
		for (let i = fractionValues.length - 1; i >= 0; i--) {
			if (input >= fractionValues[i]) {
				return fractions[i];
			}
		}
		return "0";
	}
	if (width <= 30) x = 30;
	else if (width <= 38) x = 38;
	else if (width <= 54) x = 54;
	else x = 72;
	y = measure === "Tight" ? 0.125 : 0;
	const cutSize = (x - width + y) / 2;
	const integerPart = Math.floor(cutSize);
	const fractionalPartValue = cutSize - integerPart;
	const fractionString = roundDownToFraction(fractionalPartValue);
	return fractionString === "0"
		? `${integerPart}`
		: `${integerPart} ${fractionString}`;
}

// --- Breakdown Component (Revised slightly for modal context) ---
const Breakdown = ({
	renderFabric,
	renderHem,
	renderValance,
	prices,
	displayCountryPrice,
	isCellular,
	isLoading,
	isModal = false, // Flag for modal styling adjustments
}) => {
	const {
		retailPrice = 0,
		finalPrice = 0,
		savings = 0,
		components = {},
	} = prices || {};
	const {
		fabricPrice = 0,
		drivePrice = 0,
		remotePrice = 0,
		chargerPrice = 0,
		connectorPrice = 0,
		valancePrice = 0,
		channelPrice = 0,
	} = components;
	const controlPriceTotal =
		parseFloat(drivePrice) +
		parseFloat(remotePrice) +
		parseFloat(chargerPrice) +
		parseFloat(connectorPrice);
	const valancePriceTotal = parseFloat(valancePrice) + parseFloat(channelPrice);
	const savingsPercentage =
		retailPrice > 0
			? (((retailPrice - finalPrice) / retailPrice) * 100).toFixed(0)
			: 0;

	return (
		<div className="space-y-4 md:sticky md:top-8">
			{/* Preview Window */}
			<motion.div className="h-[450px] md:h-[550px] bg-gray-100 rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
				<div className="relative overflow-hidden">
					{/* Optional: Add a loading overlay for the render */}
					{/* {isLoading && <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>} */}
					<BlindRender
						renderFabric={renderFabric}
						renderHem={renderHem}
						renderValance={renderValance}
					/>
				</div>
			</motion.div>
			{/* Price Summary Card */}
			<div
				className={`bg-white md:bg-gray-50 rounded-2xl p-5 md:px-2 md:py-4 text-gray-800 shadow-sm border border-gray-200 md:border-none md:shadow-none transition-opacity duration-200 ${
					isLoading ? "opacity-50" : "opacity-100"
				}`}
			>
				<h3 className="text-lg font-semibold mb-4 border-b pb-2 flex justify-between items-center">
					Price Summary
					{isLoading && (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary ml-2"></div>
					)}
				</h3>
				<div className="space-y-2 text-sm">
					{/* ... (rest of breakdown remains the same) ... */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">Base Fabric</span>
						<span>{displayCountryPrice(fabricPrice)}</span>
					</div>
					{!isCellular && (
						<>
							<div className="flex justify-between items-center">
								<span className="text-gray-600">Bottom Bar</span>
								<span className="text-green-600 font-medium">Included</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-gray-600">Control System</span>
								<span>
									{controlPriceTotal > 0
										? `+ ${displayCountryPrice(controlPriceTotal)}`
										: "-"}
								</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-gray-600">Valance/Cassette</span>
								<span>
									{valancePriceTotal > 0
										? `+ ${displayCountryPrice(valancePriceTotal)}`
										: "-"}
								</span>
							</div>
						</>
					)}
					<div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
						<div className="flex justify-between items-center">
							<span className="text-gray-600">Retail Price</span>
							<span className="line-through">
								{displayCountryPrice(retailPrice)}
							</span>
						</div>
						{savings > 0 && (
							<div className="flex justify-between items-center text-red-600">
								<span>You Save ({savingsPercentage}%)</span>
								<span>- {displayCountryPrice(savings)}</span>
							</div>
						)}
					</div>
					<div className="border-t border-gray-200 pt-3 mt-3">
						<div className="flex justify-between items-center text-lg md:text-xl">
							<span className="font-semibold">Your Price</span>
							<span className="font-bold text-primary">
								{displayCountryPrice(finalPrice)}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

// --- Main Layout Component ---
const Customize_Reworked = ({ children }) => {
	/*===============================  VARIABLES  =================================*/
	const dispatch = useDispatch();
	const history = useNavigate();
	const location = useLocation();

	const userInfo = useSelector((state) => state.userLogin.userInfo);
	const country = useSelector((state) => state.country);
	const productInfo = useSelector(
		(state) => state.customize?.customizeProduct?.productInfo || {}
	);
	const optionInfo = useSelector(
		(state) => state.customize?.customizeProduct?.optionInfo || {}
	);

	// Extract necessary properties with defaults
	const {
		Type: type,
		Fabric: fabric,
		SG_Color: sgColor,
		Bottom_Bar: bottomBar,
		Bottom_Bar_Color: bottomBarColor,
		Hem_Wrap_Type: hemWrapType,
		Valance: valance,
		Valance_Color: valanceColor,
		Fabric_Code: fabricCode,
		Price_Group: priceGroup = -1,
		Operation: operation,
		L_Channel: lChannel,
		remote,
		charger,
		connector,
		Discount: productDiscount,
		Global_Discount: globalDiscount,
	} = productInfo;
	const {
		widthWhole,
		widthFraction,
		lengthWhole,
		lengthFraction,
		quantity = 1,
		measure,
		editId,
	} = optionInfo;

	// Component State
	const [totalPrice, setTotalPrice] = useState(0);
	const [componentPrices, setComponentPrices] = useState({});
	const [currentPage, setCurrentPage] = useState(1);
	const [renderFabric, setRenderFabric] = useState("");
	const [renderHem, setRenderHem] = useState("");
	const [renderValance, setRenderValance] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
	// *** State for mobile preview modal ***
	const [showMobilePreview, setShowMobilePreview] = useState(false);

	const params = useMemo(
		() => new URLSearchParams(location.search),
		[location.search]
	);
	const defaultConfig = params.get("default");
	const isCellular = type === "Simply Cellular";
	const steps = useMemo(() => {
		/* ... steps definition ... */
		const baseSteps = [
			{
				page: 1,
				name: "Details",
				url: "/details",
				info: "Enter dimensions and select mount type.",
			},
			{
				page: 2,
				name: "Fabric",
				url: "/fabric",
				info: "Choose your desired fabric style and color.",
			},
			{
				page: 3,
				name: "Bottom Bar",
				url: "/bottombar",
				info: "Select the bottom bar style and color.",
			},
			{
				page: 4,
				name: "Control",
				url: "/control",
				info: "Choose how you want to operate your shade.",
			},
			{
				page: 5,
				name: "Valance",
				url: "/valance",
				info: "Select a valance or cassette to cover the top roll.",
			},
			{
				page: 6,
				name: "Summary",
				url: "/summary",
				info: "Review your selections and add to cart.",
			},
		];
		const filteredSteps = isCellular
			? baseSteps.filter((step) => ![3, 4, 5].includes(step.page))
			: baseSteps;
		return filteredSteps.map((step, i) => ({ ...step, page: i + 1 }));
	}, [isCellular]);
	const isLastStep = currentPage === steps.length;

	/*===============================  USE EFFECT HOOKS ================================*/
	// --- Update current page based on URL ---
	useEffect(() => {
		/* ... logic to set currentPage ... */
		const currentPath = location.pathname.split("/customize")[1] || "/details";
		const stepMatch = steps.find((step) => step.url === currentPath);
		if (stepMatch) {
			setCurrentPage(stepMatch.page);
		} else if (steps.length > 0) {
			history(`/customize${steps[0].url}?${params.toString()}`, {
				replace: true,
			});
		}
	}, [location.pathname, steps, history, params]);

	// --- Pricing Calculation ---
	const getDimensionValue = useCallback((whole, fraction) => {
		/* ... function ... */
		const wholeNum = parseFloat(whole) || 0;
		let fracValue = 0;
		if (fraction && fraction !== "0") {
			try {
				fracValue = new Function(`return ${fraction}`)() || 0;
			} catch (e) {
				console.error("Error evaluating fraction:", fraction, e);
			}
		}
		return wholeNum + fracValue;
	}, []);
	const getPrice = useCallback(async () => {
		/* ... getPrice logic ... */
		const width = getDimensionValue(widthWhole, widthFraction);
		const length = getDimensionValue(lengthWhole, lengthFraction);
		if (width <= 0 || length <= 0) {
			return;
		}
		setIsCalculatingPrice(true);
		try {
			const apiParams = {
				length,
				width,
				shade: `${type} Shade`,
				priceGroup,
				drive: operation,
				valance,
				channel: lChannel || "None",
				remote: remote || "No",
				charger: charger || "No",
				connector: connector || "No",
			};
			let priceResult = await calculateTotalPrice(
				apiParams.length,
				apiParams.width,
				apiParams.shade,
				apiParams.priceGroup,
				apiParams.drive,
				apiParams.valance,
				apiParams.channel,
				apiParams.remote,
				apiParams.charger,
				apiParams.connector
			);
			const factor = 1.5;
			let calculatedComponentPrices = {};
			let calculatedTotalPrice = 0;
			if (
				priceResult &&
				typeof priceResult === "object" &&
				priceResult.totalPrice !== undefined
			) {
				Object.keys(priceResult).forEach((key) => {
					const price =
						(parseFloat(priceResult[key]) || 0) *
						parseInt(quantity || 1) *
						factor;
					calculatedComponentPrices[key] = price.toFixed(2);
				});
				calculatedTotalPrice =
					(parseFloat(priceResult.totalPrice) || 0) *
					parseInt(quantity || 1) *
					factor;
				setComponentPrices(calculatedComponentPrices);
				setTotalPrice(calculatedTotalPrice);
			} else {
				throw new Error("Invalid price data received from API.");
			}
		} catch (error) {
			console.error("Error in getPrice -> calculateTotalPrice:", error);
			setComponentPrices({});
			setTotalPrice(0);
		} finally {
			setIsCalculatingPrice(false);
		}
	}, [
		type,
		fabricCode,
		priceGroup,
		operation,
		valance,
		lChannel,
		remote,
		charger,
		connector,
		widthWhole,
		widthFraction,
		lengthWhole,
		lengthFraction,
		quantity,
		getDimensionValue,
	]);

	useEffect(() => {
		console.log("LOLL", fabricCode, totalPrice);
		if (!fabricCode && totalPrice) {
			setTotalPrice(0);
			return;
		}
		getPrice();
	}, [
		getPrice,
		defaultConfig,
		type,
		fabricCode,
		priceGroup,
		widthWhole,
		lengthWhole,
		operation,
		valance,
	]);
	const priceDetails = useMemo(() => {
		if (!fabricCode) return {};
		/* ... calculation using totalPrice, discounts, componentPrices ... */
		const retail = parseFloat(totalPrice) || 0;
		const discount1 = parseFloat(productDiscount) || 0;
		const discount2 = parseFloat(globalDiscount) || 0;
		const savings1 = retail * discount1;
		const priceAfterDiscount1 = retail - savings1;
		const savings2 = priceAfterDiscount1 * discount2;
		const final = priceAfterDiscount1 - savings2;
		const totalSavings = savings1 + savings2;
		return {
			retailPrice: retail,
			finalPrice: final > 0 ? final : 0,
			savings: totalSavings > 0 ? totalSavings : 0,
			components: componentPrices,
		};
	}, [
		fabricCode,
		totalPrice,
		productDiscount,
		globalDiscount,
		componentPrices,
	]);

	// --- Render Preview Updates ---
	useEffect(() => {
		/* ... update renderFabric ... */
		if (fabric && sgColor) {
			const newRenderFabric = `${fabric} ${sgColor}`;
			if (renderFabric !== newRenderFabric) {
				setRenderFabric(newRenderFabric);
			}
		} else if (renderFabric !== "") {
			setRenderFabric("");
		}
	}, [fabric, sgColor, renderFabric]);
	useEffect(() => {
		/* ... update renderHem ... */
		if (!isCellular && bottomBar && hemWrapType && bottomBarColor) {
			let temp = "";
			const lcBottomBar = bottomBar.toLowerCase();
			if (hemWrapType === "Yes" && lcBottomBar.includes("accubar"))
				temp = "wab";
			else if (lcBottomBar.includes("accubar")) temp = "ab";
			else if (lcBottomBar.includes("plain hem")) temp = "ph";
			else if (lcBottomBar.includes("illusion")) temp = "ill";
			else if (lcBottomBar.includes("interlude")) temp = "int";
			else temp = "ph";
			const renderColor =
				bottomBar === "Plain Hem"
					? "standard"
					: (bottomBarColor || "white").toLowerCase();
			const newRenderHem = `${temp}/${renderColor}`;
			if (renderHem !== newRenderHem) {
				setRenderHem(newRenderHem);
			}
		} else if (renderHem !== "") {
			setRenderHem("");
		}
	}, [isCellular, bottomBar, hemWrapType, bottomBarColor, renderHem]);
	useEffect(() => {
		/* ... update renderValance ... */
		if (!isCellular && valance && valanceColor) {
			const lcValance = (valance || "").toLowerCase();
			const lcColor = (valanceColor || "white").toLowerCase();
			const nameMap = {
				"open roll": "or",
				'3" fascia': "3f",
				'4" fascia': "4f",
				"compact curved cassette": "ccc",
				"wrapped compact curved cassette": "wccc",
				"pvc valance": "pvc",
				"curved cassette": "ccc",
				"wrapped curved cassette": "wcc",
				"square cassette": "sc",
				"wrapped square cassette": "wsc",
				"compact square cassette": "csc",
				"wrapped compact square cassette": "wcsc",
			};
			const shortenedName = nameMap[lcValance] || "or";
			const newRenderValance = `render/valance/${shortenedName}/${lcColor}.png`;
			if (renderValance !== newRenderValance) {
				setRenderValance(newRenderValance);
			}
		} else if (renderValance !== "") {
			setRenderValance("");
		}
	}, [isCellular, valance, valanceColor, renderValance]);

	// --- Submission & Navigation ---
	function splitFullName(fullName) {
		/* ... function ... */
		if (typeof fullName !== "string" || !fullName.trim()) {
			return { fname: "Guest", lname: "" };
		}
		const parts = fullName.trim().split(/\s+/);
		const fname = parts[0];
		const lname = parts.slice(1).join(" ");
		return { fname, lname: lname || "-" };
	}
	const submitOrder = () => {
		/* ... validation and dispatch logic ... */
		if (!isLastStep || !productInfo || !optionInfo) return;
		if (productInfo.error) {
			alert("Please re-check selections.");
			return;
		}
		if (!isCellular) {
			if (valance !== "Open Roll" && (!valanceColor || valanceColor === "-")) {
				alert("Please select a valance color.");
				return;
			}
			if (
				bottomBar !== "Plain Hem" &&
				(!bottomBarColor || bottomBarColor === "-")
			) {
				alert("Please select a bottom bar color.");
				return;
			}
		}
		if (userInfo?.email) {
			addEmailToCartMailChimp(
				userInfo.email,
				"customizing shades",
				splitFullName(userInfo.name).fname,
				splitFullName(userInfo.name).lname
			);
		}
		const cartItem = {
			productInfo: {
				...productInfo,
				Program: `Customized ${type || ""} Shade`,
				New_Fabric: productInfo.OCS_Name,
				specialInstruction: isCellular
					? `Cut Per Side: ${getSimplyCutSize(
							getDimensionValue(widthWhole, widthFraction),
							measure
					  )}`
					: "",
				New_Colour: productInfo?.OCS_Colour || sgColor || "",
			},
			optionInfo: {
				...optionInfo,
				OUR_PRICE: (priceDetails.finalPrice / optionInfo.quantity).toFixed(2),
				RETAIL_PRICE: (priceDetails.retailPrice / optionInfo.quantity).toFixed(
					2
				),
			},
		};
		if (editId != null) {
			dispatch(removeFromCart(editId));
		}
		dispatch(addToCart(cartItem));
		setSubmitted(true);
		setTimeout(() => {
			window.location.href = "/cart";
		}, 1000);
	};
	const onStepClick = (step) => {
		/* ... navigation logic ... */
		if (!step || !step.url) return;
		history(`/customize${step.url}?${params.toString()}`);
	};
	const displayCountryPrice = useCallback(
		(price) => {
			/* ... price formatting ... */
			const numPrice = parseFloat(price) || 0;
			if (country === "USA") {
				return `$${(numPrice * 0.75).toFixed(2)} USD`;
			} else {
				return `$${numPrice.toFixed(2)} CAD`;
			}
		},
		[country]
	);

	/*===============================  RENDER ====================================*/
	return (
		<div className="relative min-h-screen bg-gray-50">
			<Helmet>
				{" "}
				<title>Customize Your {type || ""} Shade | Off Cut Shades</title>{" "}
				<meta
					name="description"
					content={`Design your custom ${type || "window"} shade step-by-step.`}
				/>{" "}
			</Helmet>
			{/* --- Stepper --- */}
			<div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
				{" "}
				<div className="container mx-auto px-4 py-3">
					{" "}
					<Stepper
						steps={steps}
						currentStep={currentPage}
						onStepClick={onStepClick}
					/>{" "}
				</div>{" "}
			</div>
			{/* --- Main Content --- */}
			<div className="container mx-auto max-w-7xl px-4 py-6 md:py-10">
				{/* REMOVED Mobile Preview/Price from here */}
				{/* <div className="md:hidden mb-6"> ... </div> */}

				{/* Desktop Layout */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					{" "}
					{/* Reduced gap slightly */}
					{/* Left: Preview (Desktop Only) */}
					<div className="max-w-[550px] hidden md:block">
						<Breakdown
							renderFabric={renderFabric}
							renderHem={renderHem}
							renderValance={renderValance}
							prices={priceDetails}
							displayCountryPrice={displayCountryPrice}
							isCellular={isCellular}
							isLoading={isCalculatingPrice}
							isModal={false} // Not in modal
						/>
					</div>
					{/* Right: Form Steps */}
					<div className="order-1 md:order-2">
						{" "}
						{/* Form still comes first in source for mobile focus */}
						<AnimatePresence mode="wait">
							<motion.div
								key={currentPage}
								/* ... animations ... */ className="space-y-6 md:space-y-8"
							>
								{/* Content Card */}
								<div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100">
									{/* ... Header ... */}
									<div className="mb-6 pb-4 border-b border-gray-200">
										{" "}
										<h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
											{" "}
											Step {currentPage}: {steps[currentPage - 1]?.name}{" "}
										</h1>{" "}
										<p className="text-sm text-gray-600">
											{" "}
											{steps[currentPage - 1]?.info}{" "}
										</p>{" "}
									</div>
									{/* Child Component */}
									<div className="space-y-6"> {children} </div>
								</div>
								{/* Desktop Nav Buttons */}
								<div className="hidden md:flex items-center justify-between gap-4 mt-8">
									{/* ... Back Button ... */}
									{currentPage > 1 ? (
										<motion.button
											/* ... */ onClick={() =>
												onStepClick(steps[currentPage - 2])
											}
											className="flex items-center gap-2 px-6 py-2.5 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-all group"
										>
											{" "}
											<ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />{" "}
											Back{" "}
										</motion.button>
									) : (
										<div></div>
									)}
									{/* ... Continue/Submit Button ... */}
									<motion.button
										/* ... */ onClick={() =>
											isLastStep
												? submitOrder()
												: onStepClick(steps[currentPage])
										}
										disabled={submitted || isCalculatingPrice}
										className={`flex items-center gap-2 px-7 py-3 bg-primary text-white rounded-lg font-semibold shadow-md hover:bg-primary/90 transition-all group ${
											submitted || isCalculatingPrice
												? "opacity-50 cursor-not-allowed"
												: ""
										}`}
									>
										{" "}
										{isCalculatingPrice
											? "Calculating..."
											: submitted
											? "Adding..."
											: isLastStep
											? "Add to Cart"
											: "Continue"}{" "}
										{!submitted && !isCalculatingPrice && (
											<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
										)}{" "}
									</motion.button>
								</div>
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</div>

			{/* --- Mobile Footer --- */}
			<div className="md:hidden sticky bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 z-40 shadow-[-2px_0px_10px_rgba(0,0,0,0.1)]">
				<div className="flex items-center justify-between gap-3">
					{/* *** Clickable Price/Preview Trigger *** */}
					<button
						type="button" // Important: prevent form submission
						onClick={() => setShowMobilePreview(true)}
						className="flex-shrink-0 text-left group"
						aria-label="Show price breakdown and preview"
					>
						<div className="text-xs text-gray-500 group-hover:text-primary transition-colors ml-16">
							Your Price
						</div>
						<div
							className={`text-lg font-bold text-primary mt-1 flex items-center gap-1  ml-16 ${
								isCalculatingPrice ? "opacity-50" : ""
							}`}
						>
							{isCalculatingPrice
								? "..."
								: displayCountryPrice(priceDetails.finalPrice)}
						</div>
						<span className="text-xs text-gray-600 ml-16">
							Touch for preview
						</span>
					</button>
					{/* Navigation Buttons */}
					<div className="flex gap-2 flex-grow justify-end">
						{currentPage > 1 && (
							<motion.button
								/* ... */ onClick={() => onStepClick(steps[currentPage - 2])}
								className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium text-sm"
							>
								{" "}
								Back{" "}
							</motion.button>
						)}
						<motion.button
							/* ... */ disabled={submitted || isCalculatingPrice}
							onClick={() =>
								isLastStep ? submitOrder() : onStepClick(steps[currentPage])
							}
							className={`px-5 py-2.5 bg-primary text-white rounded-lg font-semibold text-sm flex-grow ${
								submitted || isCalculatingPrice
									? "opacity-50 cursor-not-allowed"
									: ""
							}`}
						>
							{" "}
							{isCalculatingPrice
								? "Calculating..."
								: submitted
								? "Adding..."
								: isLastStep
								? "Add to Cart"
								: "Continue"}{" "}
						</motion.button>
					</div>
				</div>
			</div>

			{/* --- Mobile Preview/Breakdown Modal --- */}
			<AnimatePresence>
				{showMobilePreview && (
					<motion.div
						key="mobile-preview-backdrop"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 md:hidden" // Only show on mobile
						onClick={() => setShowMobilePreview(false)} // Close on backdrop click
					>
						<motion.div
							key="mobile-preview-content"
							initial={{ scale: 0.9, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.9, opacity: 0 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
							className="bg-gray-50 rounded-2xl w-full max-w-sm p-4 max-h-[85vh] overflow-y-auto relative shadow-xl"
							role="dialog"
							aria-modal="true"
							aria-labelledby="mobile-preview-title"
						>
							{/* Modal Header */}
							<div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
								<h2 id="mobile-preview-title" className="font-semibold text-lg">
									Preview & Breakdown
								</h2>
								<button
									onClick={() => setShowMobilePreview(false)}
									className="p-1 text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-100"
									aria-label="Close preview"
								>
									<XIcon size={20} />
								</button>
							</div>
							{/* Breakdown Component Inside Modal */}
							<Breakdown
								renderFabric={renderFabric}
								renderHem={renderHem}
								renderValance={renderValance}
								prices={priceDetails}
								displayCountryPrice={displayCountryPrice}
								isCellular={isCellular}
								isLoading={isCalculatingPrice}
								isModal={true} // Pass flag for potential style adjustments
							/>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* --- Toast --- */}
			<AnimatePresence>
				{" "}
				{submitted && (
					<Toast show={submitted} type="success" message="Added to cart!" />
				)}{" "}
			</AnimatePresence>
		</div>
	);
};

export default Customize_Reworked;
